/**
 * @providesModule AbstractEditProfileScreen
 *
 */

import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import styles from 'EditProfileScreenStyle';
import HighlightArrow from 'HighlightArrow';
import I18n, {_m} from 'INTL';
import Label from 'Label';
import LoadingIndicator from 'LoadingIndicator';
import Optional from 'Optional';
import PushButton from 'PushButton';
import {SKZGameMode} from 'SKZGameMode';
import SKZTournament, {TournamentRecord} from 'SKZTournament';
import {SKZUserInterface} from 'SKZUser';
import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzSVG from 'SkillzSVG';
import SkillzStyle from 'SkillzStyle';
import _ from 'lodash';
import React from 'react';
import Alert from 'Alert';

import {
  NativeModules,
  TextInput,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
  findNodeHandle,
  Platform,
} from 'react-native';

import {ImageLibraryOptions, launchImageLibrary} from 'react-native-image-picker';
import {getRandomAvatar} from 'RandomizeAvatarUtil';
import GlobalEventEmitter from 'GlobalEventEmitter';

const {btnGradientOne, viewCornerRadiusSecondary} = SkillzStyle.fullStyles;

const UIManager = NativeModules.UIManager;

// Copied from FTUETournamentController because we can't import it like a normal person. doing that creates
// a require cycle that prevents EditProfile subclasses from intitializing
type FTUETournamentsResponse = {
  async: TournamentRecord;
  sync: {gameMode: SKZGameMode; tournament: SKZTournament} | null;
};

const ERROR_TITLE = I18n.message('Error');
const ERROR_MESSAGE = I18n.message("Uh oh! Doesn't look like that worked. Please try again.");
const INVALID_USERNAME_MESSAGE = I18n.message('This username already exists or is invalid.');
const INVALID_LENGTH = I18n.message('Username must be between 2 and 26 characters');
const INVALID_SYMBOL = I18n.message(
  'Username must only contain letters, numbers, hyphens (-), underscores (_), or tildes (~)'
);
const ACCOUNT_CREATION_TITLE = I18n.message('Pick A Name & An Avatar');
const AVATAR_SELECTION_TITLE = I18n.message('Pick An Avatar');
const USERNAME_SELECTION_TITLE = I18n.message('Pick A Username');

const TEST_IDS = {
  EDIT_PROFILE_SCREEN_USERNAME_INPUT: 'username_input',
  EDIT_PROFILE_SCREEN_SUBMIT_BUTTON: 'submit_button',
  EDIT_PROFILE_SCREEN_PICK_INFO_TITLE: 'pick_info_title',
  EDIT_PROFILE_SCREEN_CONTAINER: 'edit_profile_container',
  EDIT_PROFILE_SCREEN_CHANGE_MESSAGE: 'change_message',
  EDIT_PROFILE_SCREEN_PICK_AVATAR: 'pick_avatar',
};

export interface AbstractEditProfileScreenProps {
  initialUser: SKZUserInterface;
  isFTUE?: boolean | null;
}

export interface AbstractEditProfileScreenState {
  username: string;
  avatar_url: string | undefined;
  canChangeUsername: boolean;
  nextButtonLayout?: Layout;
  randomUsername: string;
}

type ArrowPosition = {direction: 'top' | 'bottom'; offset: {x: number; y: number}};

type Layout = {
  x: number;
  y: number;
  width: number;
  height: number;
  rootX: number;
  rootY: number;
};

export default class AbstractEditProfileScreen<
  Props extends AbstractEditProfileScreenProps,
  State extends AbstractEditProfileScreenState,
> extends React.Component<Props, State> {
  arrow: ArrowPosition;
  isFirstTimeLaunching: boolean | null;
  firstFTUETournament?: TournamentRecord;
  /**
   * @deprecated
   * This has been replaced with a shouldTransitionToVersus parameter in
   * _handleTournamentFetch. This is only set in CreateProfileScreen.tsx, which
   * should be deleted along with this flag since we use
   * CreateProfileScreenV2.tsx now
   */
  shouldTransitionOnFetchCompletion?: boolean;
  avatarPath?: string;
  didCancel?: boolean;
  nextButtonRef?: React.Component<any, any>;

  static defaultProps = {
    isFTUE: true,
  };

  constructor(props: AbstractEditProfileScreenProps) {
    super(props as any);

    this.arrow = {direction: 'bottom', offset: {x: 0, y: 0}}; // can be overridden
    this.isFirstTimeLaunching = null;
  }

  // Always include this when setting up state in constuctor of children classes (or override)
  // this.state = {
  //   ...this._initialState(props),
  //   // other state values
  // }
  _initialState(props: AbstractEditProfileScreenProps): AbstractEditProfileScreenState {
    return {
      username: props.initialUser.username,
      avatar_url: props.initialUser.avatar_url || (props.initialUser as any).avatarUrl,
      canChangeUsername: true,
      randomUsername: props.initialUser.username,
    };
  }

  componentDidMount() {
    if (this.props.isFTUE) {
      this._attemptFirstTournamentFetch();
    }
  }

  _attemptFirstTournamentFetch = async (shouldTransitionToVersus = false) => {
    // Gotta inline-require FTUETournamentController to break a require cycle that was preventing
    // EditProfile classes from intitializing properly
    const FTUETournamentController = require('FTUETournamentController')?.default; // eslint-disable-line global-require

    if (!FTUETournamentController) {
      console.warn('FTUETournamentController not available; skipping initial tournament fetch');
      GlobalEventEmitter.emit('EndFirstFTUEMatch');
      return Promise.resolve();
    }
    return FTUETournamentController.getTournamentsForFirstFTUEMatch(
      this.props.initialUser.z_balance
    )
      .then((ftueTournaments: FTUETournamentsResponse) =>
        this._handleTournamentFetch(ftueTournaments, shouldTransitionToVersus)
      )
      .catch((error: Error) => {
        GlobalEventEmitter.emit('EndFirstFTUEMatch');
        FTUETournamentController.showNoAvailableTournamentsError(
          this._attemptFirstTournamentFetch,
          error
        );
      });
  };

  async _handleTournamentFetch(
    ftueTournaments: FTUETournamentsResponse,
    shouldTransitionToVersus = false
  ) {
    this.firstFTUETournament = ftueTournaments.async;

    if (this.shouldTransitionOnFetchCompletion || shouldTransitionToVersus) {
      LoadingIndicator.hide();
      return this._pushVersusFTUE();
    }
  }

  _pushVersusFTUE(): void | Promise<void> {
    // IMPLEMENT IN SUBCLASS
  }

  _validateUsername(): boolean {
    const length = /^.{2,26}$/;
    const characters = /^[a-zA-Z0-9_\-~]*$/;
    if (!this.state.username.match(length)) {
      this._displayError(INVALID_LENGTH, ERROR_TITLE, null, false);
      return false;
    } else if (!this.state.username.match(characters)) {
      this._displayError(INVALID_SYMBOL, ERROR_TITLE, null, false);
      return false;
    } else {
      return true;
    }
  }

  // submit changes to username/avatar to TS
  _submitProfileChanges(completion: (success: boolean) => void) {
    let updates: Promise<any>[] = [];
    updates = this._getAvatarUpdate(updates);
    if (this.state.username !== this.props.initialUser.username) {
      const usernameValidation = this._validateUsername();
      if (!usernameValidation) {
        completion(false);
        return;
      } else {
        updates = this._getUsernameUpdate(updates);
      }
    }
    if (updates.length === 0) {
      console.debug('Edit profile: will not update username or avatar in ts');
      completion(true);
      return;
    } else {
      this._doProfileUpdatesInternal(updates, completion);
    }
  }

  _doProfileUpdatesInternal(updates: Promise<any>[], completion: (success: boolean) => void) {
    LoadingIndicator.show();
    Promise.all(updates)
      .then(() => {
        CurrentUserStore.updateUserData({username: this.state.username});
        LoadingIndicator.hide();
        completion(true);
      })
      .catch((error: string) => {
        LoadingIndicator.hide();
        console.warn('Error updating user information in edit profile:', error);
        if (error?.includes('username')) {
          this._displayError(INVALID_USERNAME_MESSAGE, ERROR_TITLE, null, false);
          completion(false);
        } else {
          this._displayError(
            ERROR_MESSAGE,
            ERROR_TITLE,
            this._submitProfileChanges.bind(this, completion),
            true
          );
        }
      });
  }

  _getAvatarUpdate(updates: Promise<any>[]): Promise<any>[] {
    if (this.state.avatar_url !== this.props.initialUser.avatar_url) {
      console.debug('Edit Profile: will update avatar in ts');
      const avatarPath = this.state.avatar_url?.replace(/^file:\/\//, '');
      updates.push(CurrentUserStore.updateAvatar(avatarPath, this.state.avatar_url));
      return updates;
    } else {
      return updates;
    }
  }

  _getUsernameUpdate(updates: Promise<any>[]): Promise<any>[] {
    console.debug(`Edit Profile: will update username in ts to ${this.state.username}`);
    updates.push(CurrentUserStore.updateUser({username: this.state.username}));
    return updates;
  }

  // The callback and twoButtons parameters are used in components that inherit AbstractEditProfileScreen
  _displayError(
    errorMessage: string,
    title: string,
    _callback?: (() => void) | null,
    _twoButtons?: boolean
  ) {
    Alert.alert(title, errorMessage);
  }

  _handleUsernameChange = (text: string) => {
    this.setState({username: text});
  };

  _canPressNext(usernameSelection?: boolean): boolean {
    // when split account enabled, only disable button for username selection screen when username not chosen yet
    const condition =
      ClientPropertyConstants.getFTUESplitAccountCreationEnabled() &&
      usernameSelection !== undefined
        ? !!this.state.username || !usernameSelection
        : !!this.state.username;
    return condition;
  }

  _showImagePicker = () => {
    if (Platform.OS === 'web') {
      this._showWebPicker();
    } else {
      this._showNativePicker();
    }
  };

  _showNativePicker = async () => {
    const imagePickerOptions: ImageLibraryOptions = {
      mediaType: 'photo',
      selectionLimit: 1,
      maxWidth: 1024,
      maxHeight: 1024,
      //@ts-ignore this is not documented in the react-native-image-picker library but it is still supported natively
      // and is required for image cropping
      allowsEditing: 'true',
    };

    LoadingIndicator.show();
    let result;
    try {
      result = await launchImageLibrary(imagePickerOptions);
    } catch (error) {
      LoadingIndicator.hide();
      console.debug('FTUE: error in image picker: ', error);
    }

    if (!result) {
      return;
    }

    if (result.assets || (result as any).uri) {
      let imgUri: string | undefined = result?.assets?.[0]?.uri;
      if (imgUri) {
        this.setState({avatar_url: imgUri}, () => {
          LoadingIndicator.hide();
        });
      } else {
        LoadingIndicator.hide();
      }
    } else if (result.didCancel) {
      this.didCancel = true;
      LoadingIndicator.hide();
    } else {
      LoadingIndicator.hide();
    }
  };

  _showWebPicker = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';

    input.onchange = async (event: Event) => {
      console.log('avatar onchange: ', event);
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onloadend = (readerEvent) => {
        console.log('avatar on load end: ', readerEvent);
        this.setState({avatar_url: reader.result as string});
      };
      reader.onerror = () => {
        console.error('avatar Error reading file.');
        document.body.removeChild(input);
      };
      reader.readAsDataURL(file);
      document.body.removeChild(input);
    };

    document.body.appendChild(input);
    input.click();
  };

  _updateNextButtonRef(ref?: React.Component<any, any> | null) {
    if (ref) {
      this.nextButtonRef = ref;
    }
    if (this.nextButtonRef) {
      const node = findNodeHandle(this.nextButtonRef);
      if (node) {
        UIManager.measure(
          node,
          (x: number, y: number, width: number, height: number, rootX: number, rootY: number) => {
            const layout = {
              x: x,
              y: y,
              width: width,
              height: height,
              rootX: rootX,
              rootY: rootY,
            };
            if (!_.isEqual(layout, this.state.nextButtonLayout)) {
              this.setState({
                nextButtonLayout: layout,
              });
            }
          }
        );
      }
    }
  }

  _renderPickInfoTitle(title: string): React.ReactElement {
    return (
      <View
        accessible={true}
        accessibilityLabel={title}
        testID={TEST_IDS.EDIT_PROFILE_SCREEN_PICK_INFO_TITLE}
      >
        <Label style={styles.pickInfoTitle}>{title}</Label>
      </View>
    );
  }

  _renderPickAvatar(): React.ReactElement {
    console.debug('RandomizeAvatarUtil: getRandomAvatar: avatarPath: ', this.state.avatar_url);
    return (
      <View
        style={styles.avatarContainer}
        accessible={true}
        testID={TEST_IDS.EDIT_PROFILE_SCREEN_PICK_AVATAR}
        accessibilityLabel={'pick_avatar'}
      >
        <SkillzImage
          style={styles.avatarImage}
          source={this.state.avatar_url ?? 'avatar_pending_generic_skz.png'}
        />
        {this._renderChangeAvatarButtons()}
      </View>
    );
  }

  _onRefreshUsernamePress = () => {
    if (this.props.isFTUE) {
      AWSManager.trackEvent('FTUE_REFRESH_USERNAME');
    } else {
      AWSManager.trackEvent('PROFILE_REFRESH_USERNAME');
    }
    CurrentUserStore.fetchRandomGeneratedUsername()
      .then((newUsername: string) => {
        this.setState({
          username: newUsername,
          randomUsername: newUsername,
        });
      })
      .catch((error: Error) => {
        console.debug('error getting random generated username.', error);
      });
  };

  _onRefreshAvatarPress = () => {
    if (this.props.isFTUE) {
      AWSManager.trackEvent('FTUE_REFRESH_AVATAR');
    } else {
      AWSManager.trackEvent('PROFILE_REFRESH_AVATAR');
    }
    LoadingIndicator.showWithText('', LoadingIndicator.Style.NoIcon);
    getRandomAvatar()
      .then((avatarPath: string) => {
        console.debug('RandomizeAvatarUtil: getRandomAvatar: avatarPath: ', avatarPath);
        this.setState({avatar_url: avatarPath}, () => {
          LoadingIndicator.hide();
        });
      })
      .catch((error) => {
        LoadingIndicator.hide();
        console.debug('error getting random generated avatar.', error);
      });
  };

  _renderChangeAvatarButtons(): React.ReactElement {
    const shouldAllowRefresh = ClientPropertyConstants.getAvatarRandomizerEnabled();
    const shouldAllowFullEdit =
      !ClientPropertyConstants.getAvatarRandomizerOnlyFTUE() || !this.props.isFTUE;

    const refreshAvatar = (
      <View style={styles.refreshAvatarIcon}>
        <TouchableOpacity onPress={this._onRefreshAvatarPress} testID={'refresh_avatar_button'}>
          <SkillzSVG style={styles.refreshButtonIconV2} source={'ic_random_button.svg'} />
        </TouchableOpacity>
      </View>
    );

    const editAvatar = (
      <View style={styles.editAvatarIcon}>
        <TouchableOpacity onPress={this._showImagePicker} testID={'edit_avatar_button'}>
          <SkillzSVG style={styles.editButtonIconV2} source={'ic_camera.svg'} />
        </TouchableOpacity>
      </View>
    );

    return (
      <View style={styles.editAvatarButtonsContainer}>
        {shouldAllowFullEdit || !shouldAllowRefresh ? editAvatar : <React.Fragment />}
        {shouldAllowRefresh ? refreshAvatar : <React.Fragment />}
      </View>
    );
  }

  _renderRefreshUsernameButton(renderBoth: boolean): React.ReactElement {
    if (
      this.state.canChangeUsername &&
      (ClientPropertyConstants.getFTUESplitAccountCreationEnabled() ||
        ClientPropertyConstants.getUsernameRandomizerEnabled())
    ) {
      const paddingTop = renderBoth && Sizes.isPhone() ? 8 : 13;
      const tabletPaddingRight = renderBoth ? 50 : 60;
      const paddingRight = Sizes.isTablet() ? tabletPaddingRight : 25;
      return (
        <View style={styles.refreshButtonContainer}>
          <TouchableOpacity
            onPress={this._onRefreshUsernamePress}
            testID={'refresh_username_button'}
            style={{
              paddingTop: Platform.OS === 'web' ? 11 : paddingTop,
              paddingRight: paddingRight,
            }}
          >
            <SkillzSVG style={styles.refreshButtonIcon} source={'ic_random_button.svg'} />
          </TouchableOpacity>
        </View>
      );
    } else {
      return <React.Fragment />;
    }
  }

  _clearTextIfRandom = () => {
    if (
      (ClientPropertyConstants.getFTUESplitAccountCreationEnabled() ||
        ClientPropertyConstants.getUsernameRandomizerEnabled()) &&
      this.state.username === this.state.randomUsername
    ) {
      this.setState({
        username: '',
      });
    }
  };

  _renderPickUsername(
    style: {changeUsername: ViewStyle; changeUsernameContainer: ViewStyle},
    renderBoth: boolean
  ): React.ReactElement {
    const opacity = this.state.canChangeUsername ? {opacity: 1} : {opacity: 0.3};
    // when randomizer enabled, left align username text
    const leftAligned: TextStyle =
      ClientPropertyConstants.getFTUESplitAccountCreationEnabled() ||
      ClientPropertyConstants.getUsernameRandomizerEnabled()
        ? {textAlign: 'left', paddingLeft: 10}
        : {};
    // when rendering both, pad bottom less
    const renderBothStyle =
      renderBoth && ClientPropertyConstants.getFTUESplitAccountCreationEnabled()
        ? {paddingBottom: 30}
        : {paddingBottom: 90};
    // when refresh button is there, make sure there's no text overlap
    const refreshPadding =
      ClientPropertyConstants.getFTUESplitAccountCreationEnabled() ||
      ClientPropertyConstants.getUsernameRandomizerEnabled()
        ? {paddingRight: 35}
        : {};
    return (
      <View style={[styles.changeUsernameContainer, renderBothStyle]}>
        {this._renderRefreshUsernameButton(renderBoth)}
        <TextInput
          style={[style.changeUsername, opacity, leftAligned, refreshPadding]}
          onFocus={this._clearTextIfRandom}
          onChangeText={this._handleUsernameChange}
          value={this.state.username}
          editable={this.state.canChangeUsername}
          accessible={true}
          accessibilityLabel={'Username text field input'}
          testID={TEST_IDS.EDIT_PROFILE_SCREEN_USERNAME_INPUT}
        />
      </View>
    );
  }

  _renderChangeMessage(changeMessage: string): React.ReactElement {
    return (
      <View
        accessible={true}
        accessibilityLabel={changeMessage}
        testID={TEST_IDS.EDIT_PROFILE_SCREEN_CHANGE_MESSAGE}
      >
        <Label style={styles.changeMessage}>{changeMessage}</Label>
      </View>
    );
  }

  _renderCenterContent(
    heading: React.ReactNode,
    onPressNext: () => void,
    changeMessage: string,
    buttonTitle: string,
    customStyle: any,
    renderTos: () => React.ReactNode | null = (): null => null,
    usernameSelection?: boolean
  ): React.ReactNode {
    // Components that inherit AbstractEditProfileScreen may have different styles, which is passed in through customStyle
    const useStyle = customStyle ? customStyle : styles;

    const noUsernamePadding = usernameSelection ? {} : {paddingBottom: 30};
    const pickInfoTitle = usernameSelection ? USERNAME_SELECTION_TITLE : AVATAR_SELECTION_TITLE;

    // render both avatar and username selection if client prop disabled or usernameSelection not given
    let renderBoth = false;
    if (
      usernameSelection == null ||
      !ClientPropertyConstants.getFTUESplitAccountCreationEnabled()
    ) {
      renderBoth = true;
    }

    return (
      <View style={useStyle.centerContentContainer}>
        {heading}
        <View style={styles.centerContent} testID={TEST_IDS.EDIT_PROFILE_SCREEN_CONTAINER}>
          <View
            style={[
              useStyle.nameAndAvatarContainer,
              ClientPropertyConstants.getFTUESplitAccountCreationEnabled() ? noUsernamePadding : {},
            ]}
          >
            {this._renderPickInfoTitle(
              ClientPropertyConstants.getFTUESplitAccountCreationEnabled()
                ? pickInfoTitle
                : ACCOUNT_CREATION_TITLE
            )}
            <Optional test={this.state.canChangeUsername}>
              {this._renderChangeMessage(changeMessage)}
            </Optional>
            <Optional test={renderBoth ? true : !usernameSelection}>
              {this._renderPickAvatar()}
            </Optional>
            <Optional test={renderBoth ? true : usernameSelection}>
              {this._renderPickUsername(useStyle, renderBoth)}
            </Optional>
          </View>
          <PushButton
            onPress={onPressNext}
            style={useStyle.nextButton}
            colors={btnGradientOne}
            cornerRadius={viewCornerRadiusSecondary.borderRadius}
            title={buttonTitle}
            enabled={this._canPressNext(usernameSelection)}
            textStyle={useStyle.nextButtonText}
            onLayout={() => {
              this._updateNextButtonRef();
            }}
            ref={(ref) => {
              this._updateNextButtonRef(ref);
            }}
            numberOfLines={1}
            accessibilityLabel={buttonTitle}
            testID={TEST_IDS.EDIT_PROFILE_SCREEN_SUBMIT_BUTTON}
          />
          {renderTos()}
        </View>
      </View>
    );
  }

  _renderHighlightArrow(): React.ReactElement {
    if (this.state.nextButtonLayout) {
      return (
        <HighlightArrow
          refLayout={this.state.nextButtonLayout}
          direction={this.arrow.direction}
          shouldAdjustForHome={false}
          offset={this.arrow.offset}
          imageURL={'ic_ftue_filled_arrow_skz.png'}
          shouldNotTheme={true}
          shouldBounce={true}
        />
      );
    } else {
      return <></>;
    }
  }

  render(): React.ReactElement {
    throw new Error('Attempting to render abstract class: AbstractEditProfileScreen');
    return <View />; // eslint-disable-line no-unreachable
  }
}
